import { useCallback, useEffect, useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import { getMessageWithPlaceholders } from 'shared-locales';
import { LoadingIndicator } from 'components/common';
import { Icon } from 'components/common/icons';
import { CalendarEventCalendarHeader } from '.';

import {
  CalendarApi,
  EventContentArg,
  EventClickArg,
  EventSourceFunc,
  MoreLinkContentArg,
  WeekNumberContentArg
} from '@fullcalendar/core';
import type { CalendarEventExtended } from 'shared-types';

const RenderEvent = (eventContent: EventContentArg) => (
  <div
    style={{
      alignItems: 'center',
      display: 'flex',
      fontSize: '1.4rem',
      fontWeight: 600,
      gap: 7,
      overflow: 'hidden',
      padding: '3px 8px 4px'
    }}
  >
    {eventContent.event.extendedProps.icon ? (
      <Icon
        icon={eventContent.event.extendedProps.icon}
        style={{ fontSize: '1rem' }}
      />
    ) : null}
    <div>{eventContent.event.title}</div>
  </div>
);

interface Props {
  getEvents: ({
    start,
    end
  }: {
    start: Date;
    end: Date;
  }) => Promise<{ events: CalendarEventExtended[] }>;
  getCalendarApi?: (calendarApi: CalendarApi) => void;
  hasCreatePermissions: boolean;
  onDateClick?: (event: DateClickArg) => void;
  onEventClick?: (event: CalendarEventExtended) => void;
  showWeekNumbers?: boolean;
}

const CalendarEventCalendar = ({
  getEvents,
  getCalendarApi,
  hasCreatePermissions,
  onDateClick,
  onEventClick,
  showWeekNumbers = true
}: Props) => {
  const { language, messages, theme } = useReduxState();

  const calendarApiRef = useRef<CalendarApi>(undefined);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => clearTimeout(fetchEventsTimeout);
  }, []);

  let fetchEventsTimeout: NodeJS.Timeout;
  const fetchEvents: EventSourceFunc = useCallback(
    (fetchInfo, onSuccess, onFailure) => {
      clearTimeout(fetchEventsTimeout);
      const { start, end } = fetchInfo;

      if (getEvents)
        fetchEventsTimeout = setTimeout(
          () =>
            getEvents({ start, end })
              .then((result) => {
                const events =
                  result.events?.map((event) => {
                    const start = new Date(event.startDate);
                    const end = new Date(event.endDate);

                    return {
                      ...event,
                      allDay: false,
                      id: `${event.id}`,
                      start,
                      end,
                      textColor: theme.textColorLight,
                      ...(event.userIsOwner
                        ? { color: theme.successLightColor, icon: 'key' }
                        : event.userStatus === 'added'
                        ? { color: theme.successLightColor, icon: 'check' }
                        : event.userStatus === 'queued'
                        ? {
                            color: theme.warningLightColor,
                            icon: ['far', 'clock']
                          }
                        : { color: theme.lightGreyDark })
                    };
                  }) || [];

                onSuccess(events);
              })
              .catch(() => onFailure(new Error(messages.commonError))),
          300
        );
      else onSuccess([]);
    },
    [getEvents]
  );

  const handleDateClick = (dateClick: DateClickArg) => {
    if (!onDateClick) return null;
    onDateClick(dateClick);
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    if (!onEventClick) return null;

    clickInfo.jsEvent.preventDefault(); // avoid automatic navigation if url prop is defined

    // remove more Popover if active when clicking an event
    const el = document.getElementsByClassName('fc-popover');
    if (el.item(0)) {
      const element = el.item(0) as HTMLDivElement;
      element.style.display = 'none';
    }

    const eventExtendedProps = {
      ...(clickInfo.event.extendedProps as Omit<
        CalendarEventExtended,
        'id' | 'title'
      >)
    };

    onEventClick({
      ...eventExtendedProps,
      id: Number(clickInfo.event.id),
      title: clickInfo.event.title
    });
  };

  const handleParseWeek = (arg: WeekNumberContentArg) =>
    getMessageWithPlaceholders({
      message: messages.eventCalendarWeekNumber || '',
      placeholders: { weekNumber: arg.num }
    });

  const handleShowMore = (arg: MoreLinkContentArg) => (
    <div
      style={{ margin: '3px 5px' }}
    >{`+ ${messages.commonShowAll} (${arg.num})`}</div>
  );

  const setCalendarApi = (calendarApi: FullCalendar) => {
    calendarApiRef.current = calendarApi?.getApi();
    if (calendarApiRef.current && getCalendarApi)
      getCalendarApi(calendarApiRef.current);
  };

  const buttonText = { today: messages.eventCalendarToday };

  const locale = language.split('-')[0];

  return (
    <div className="vuo-fullcalendar">
      <CalendarEventCalendarHeader calendarApi={calendarApiRef.current} />

      {loading ? <LoadingIndicator /> : null}

      <div style={{ display: loading ? 'none' : 'inline' }}>
        <FullCalendar
          buttonText={buttonText}
          dateClick={handleDateClick}
          dayHeaderClassNames="day-header"
          dayMaxEvents={5}
          eventClick={handleEventClick}
          eventContent={RenderEvent}
          eventDisplay="block"
          events={fetchEvents}
          headerToolbar={null}
          height="auto"
          initialView="dayGridMonth"
          lazyFetching={true}
          loading={(isLoading: boolean) => setLoading(isLoading)}
          locale={locale}
          moreLinkContent={handleShowMore}
          plugins={[dayGridPlugin, interactionPlugin]}
          ref={setCalendarApi}
          weekNumbers={showWeekNumbers}
          weekNumberCalculation="ISO"
          weekNumberContent={handleParseWeek}
        />
      </div>

      <style global jsx>{`
        .vuo-fullcalendar .fc {
          font-size: 1.6rem;
          font-family: inherit;
          padding: 20px;
          //padding: 20px 0 0;
        }
        .vuo-fullcalendar .day-header {
          background-color: ${theme.lightGreyLighter};
        }
        .vuo-fullcalendar .fc .fc-scrollgrid-section-sticky > * {
          z-index: 1;
        }
        .vuo-fullcalendar .day-header .fc-scrollgrid-sync-inner {
          color: ${theme.textColorLighter};
          font-size: 1.5rem;
          padding: 3px 0;
        }
        .vuo-fullcalendar .fc .fc-daygrid-day.fc-day-today {
          background-color: ${theme.lightGrey};
        }
        .vuo-fullcalendar .fc-toolbar-title::first-letter,
        .vuo-fullcalendar .fc-col-header-cell {
          text-transform: capitalize;
        }
        .vuo-fullcalendar .fc .fc-button {
          background-color: ${theme.lightGrey};
          border: none;
          box-shadow: none !important;
          color: #5f5f5f !important;
          font-size: 1.5rem;
          outline: none;
          padding: 6px 10px;
        }
        .vuo-fullcalendar .fc .fc-button:hover:not(:disabled) {
          background-color: ${theme.lightGreyActive};
        }
        .vuo-fullcalendar .fc .fc-day {
          cursor: ${hasCreatePermissions ? 'copy' : 'default'};
        }
        .vuo-fullcalendar .fc .fc-event {
          cursor: pointer;
        }
        .vuo-fullcalendar .fc .fc-daygrid-week-number {
          background-color: ${theme.lightGreyLighter};
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: 400;
          margin: 5px;
          padding: 2px 5px;
        }
        .vuo-fullcalendar .fc .fc-daygrid-day-number {
          color: ${theme.textColorLight};
          font-size: 1.8rem;
          font-weight: 400;
          margin: 5px 10px;
        }
        .vuo-fullcalendar .fc .fc-daygrid-day-events {
          margin-top: -5px;
          margin-left: 3px;
          margin-right: 3px;
        }
        .vuo-fullcalendar .fc-daygrid-event {
          border-radius: 6px;
          margin-top: 5px;
        }
        // border-color
        .vuo-fullcalendar table.fc-scrollgrid,
        .vuo-fullcalendar .fc-scrollgrid-section-header > th,
        .vuo-fullcalendar .fc-scrollgrid-section-body > td,
        .vuo-fullcalendar .fc .fc-day {
          border-color: ${theme.lightGreyDark};
        }
        // border-radius
        .vuo-fullcalendar .fc,
        .vuo-fullcalendar .fc-view-harness,
        .vuo-fullcalendar .fc-daygrid,
        .vuo-fullcalendar table.fc-scrollgrid {
          border-radius: ${theme.cardBorderRadius};
        }
        .vuo-fullcalendar .fc-scrollgrid > thead,
        .vuo-fullcalendar .fc-scrollgrid > thead > tr,
        .vuo-fullcalendar .fc-scrollgrid > thead > tr > th,
        .vuo-fullcalendar .fc-scrollgrid > thead .fc-scroller-harness,
        .vuo-fullcalendar .fc-scrollgrid > thead .fc-scroller,
        .vuo-fullcalendar .fc-col-header,
        .vuo-fullcalendar .fc-col-header > thead,
        .vuo-fullcalendar .fc-col-header > thead > tr {
          border-radius: ${`${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0`};
        }
        .vuo-fullcalendar .fc-scrollgrid > tbody,
        .vuo-fullcalendar .fc-scrollgrid > tbody > tr,
        .vuo-fullcalendar .fc-scrollgrid > tbody > tr > td,
        .vuo-fullcalendar .fc-scrollgrid > tbody .fc-scroller-harness,
        .vuo-fullcalendar .fc-scrollgrid > tbody .fc-scroller,
        .vuo-fullcalendar .fc-daygrid-body,
        .vuo-fullcalendar .fc-daygrid-body table,
        .vuo-fullcalendar .fc-daygrid-body tbody {
          border-radius: ${`0 0 ${theme.cardBorderRadius} ${theme.cardBorderRadius}`};
        }
      `}</style>
    </div>
  );
};

export default CalendarEventCalendar;
